import * as assert from 'assert';
import { computed, observable } from 'mobx';
import { parse } from 'querystring';
import { NotificationStore } from '../Stores/NotificationStore';
import { OnboardingStore } from '../Stores/OnboardingStore';
import { StoryPhotoCommentsStore } from '../Stores/StoryPhotoCommentsStore';
import { StoryStore } from '../Stores/StoryStore';
import { UserFamilyStore } from '../Stores/UserFamilyStore';
import { UserPhotoStore } from '../Stores/UserPhotoStore';
import { UserProfileStore } from '../Stores/UserProfileStore';
import { AbstractStore } from './AbstractStore';
import { ArtifactStore } from './ArtifactStore';
import { AuthorizationStore } from './AuthorizationStore';
import { CollectionStore } from './CollectionStore';
import { FamilyManagmentStore } from './FamilyManagmentStore';
import { FamilyStore } from './FamilyStore';
import { ModalStore } from './ModalStore';
import { PasswordResetStore } from './PasswordResetStore';
import { SearchBarFilterStore } from './SearchBarFilterStore';
import { UserStore } from './UserStore';
import { ViewHistoryStore } from './ViewHistoryStore';

import { LearningCenterStore } from './LearningCenterStore';

export interface ISetupApplicationStore {
    authorizationStore: AuthorizationStore;
    initializeStore(): Promise<void>;
}

export class ApplicationStore extends AbstractStore {
    @observable
    private _authorizationStore: AuthorizationStore;
    @observable
    private _familyStore!: FamilyStore;
    @observable
    private _collectionStore!: CollectionStore;
    @observable
    private _artifactStore!: ArtifactStore;
    @observable
    private _storyPhotoCommentsStore!: StoryPhotoCommentsStore;
    @observable
    private _userStore!: UserStore;
    @observable
    private _modalStore: ModalStore;
    @observable
    private _viewHistoryStore!: ViewHistoryStore;
    @observable
    private _searchBarFilterStore!: SearchBarFilterStore;
    @observable
    private _familyManagmentStore!: FamilyManagmentStore;
    @observable
    private _passwordResetStore: PasswordResetStore;
    @observable
    private _userPhotoStore!: UserPhotoStore;
    @observable
    private _storyStore!: StoryStore;
    @observable
    private _userFamilyStore!: UserFamilyStore;
    @observable
    private _userProfileStore!: UserProfileStore;
    @observable
    private _onboardingStore: OnboardingStore;
    @observable
    private _notificationStore!: NotificationStore;
    @observable
    private _learningCenterStore!: LearningCenterStore;

    @observable
    private _authorizedStoresAreLoaded = false;

    @computed
    public get onboardingStore(): OnboardingStore {
        return this._onboardingStore;
    }

    @computed
    public get userFamilyStore(): UserFamilyStore {
        return this._userFamilyStore;
    }

    @computed
    public get storyStore(): StoryStore {
        return this._storyStore;
    }

    @computed
    public get authorizedStoresAreLoaded(): boolean {
        return this._authorizedStoresAreLoaded;
    }

    @computed
    public get userPhotoStore(): UserPhotoStore {
        return this._userPhotoStore;
    }
    @computed
    public get passwordResetStore(): PasswordResetStore {
        return this._passwordResetStore;
    }

    @computed
    public get familyManagmentStore(): FamilyManagmentStore {
        return this._familyManagmentStore;
    }

    @computed
    public get searchBarFilterStore(): SearchBarFilterStore {
        return this._searchBarFilterStore;
    }

    @computed
    public get authorizationStore(): AuthorizationStore {
        return this._authorizationStore;
    }

    @computed
    public get familyStore(): FamilyStore {
        return this._familyStore;
    }

    @computed
    public get collectionStore(): CollectionStore {
        return this._collectionStore;
    }

    @computed
    public get artifactStore(): ArtifactStore {
        return this._artifactStore;
    }

    @computed
    public get storyPhotoCommentStore(): StoryPhotoCommentsStore {
        return this._storyPhotoCommentsStore;
    }

    @computed
    public get viewHistoryStore(): ViewHistoryStore {
        return this._viewHistoryStore;
    }

    @computed
    public get userStore(): UserStore {
        return this._userStore;
    }

    @computed
    public get modalStore(): ModalStore {
        return this._modalStore;
    }

    @computed
    public get userProfileStore(): UserProfileStore {
        return this._userProfileStore;
    }

    @computed
    public get notificationStore(): NotificationStore {
        return this._notificationStore;
    }

    @computed
    public get learningCenterStore(): LearningCenterStore {
        return this._learningCenterStore;
    }

    public constructor() {
        super();
        this._authorizationStore = new AuthorizationStore(this);
        this._onboardingStore = new OnboardingStore(this);
        this._modalStore = new ModalStore();
        this._passwordResetStore = new PasswordResetStore();
    }

    public reloadData(): Promise<void> {
        return this.updateStore();
    }

    protected loadData(): Promise<void> {
        // authorization store is responsible for calling this method when we have a usable auth token.
        assert(this._authorizationStore.isAuthenticated, 'initApp called before auth');
        const token: string = this._authorizationStore.authToken;
        assert(token, 'error getting auth token from authorizationStore');
        assert(this.authorizationStore.user, 'error getting user info from authorizationStore');
        assert(this.authorizationStore.user.id, 'error getting user id from authorizationStore');
        const current_user_id: number = this.authorizationStore.user.id;

        this._searchBarFilterStore = new SearchBarFilterStore();
        this._familyManagmentStore = new FamilyManagmentStore();

        const initPromises: Array<Promise<void>> = [];

        this._userStore = new UserStore(token, this._authorizationStore);
        this._viewHistoryStore = new ViewHistoryStore(token);
        this._familyStore = new FamilyStore(token);
        // this._artifactStore = new ArtifactStore(token, current_user_id, this._tagStore, this._commentStore, this._viewHistoryStore);
        this._collectionStore = new CollectionStore(token, current_user_id, this._artifactStore, this._viewHistoryStore, this._familyStore);
        this._userPhotoStore = new UserPhotoStore(token);
        this._storyStore = new StoryStore(token);
        this._userFamilyStore = new UserFamilyStore(token);
        this._userProfileStore = new UserProfileStore(token, this._authorizationStore);
        this._storyPhotoCommentsStore = new StoryPhotoCommentsStore(token, this._userFamilyStore, this._userStore);

        const parsedQueryString = parse(window.location.search);
        this._notificationStore = new NotificationStore(token, this._userStore, this._storyStore,
            this._userFamilyStore, !!parsedQueryString.disableAutoNotificationRefresh);
        // initPromises.push(this._tagStore.initializeStore());
        // initPromises.push(this._userStore.initializeStore());
        // initPromises.push(this._commentStore.initializeStore());
        // initPromises.push(this._viewHistoryStore.initializeStore());
        // initPromises.push(this._artifactStore.initializeStore());
        // initPromises.push(this._collectionStore.initializeStore());

        this._learningCenterStore = new LearningCenterStore();

        return Promise.all(initPromises)
            .then(() => {
                this._authorizedStoresAreLoaded = true;
                return;
            })
            .catch(err => {
                console.log('ApplicationStore#loadData - initialization error: ', err);
                throw err;
            });
    }
}



// WEBPACK FOOTER //
// ./src/_stores/ApplicationStore.ts